import {
  MeetingAttemptResponseV3,
  ProductFeatureV3,
} from '@chilipiper/api-client/src/user-availability'
import {
  AlwaysInvitedUser,
  OwnershipRoutingRequest,
  PublicRoundRobinSchedulingLinkV2,
  TeamSchedulingApiClientV1,
  TeamSchedulingApiClientV2,
  TeamSchedulingApiClientV3,
} from '@chilipiper/api-client/src/team-scheduling'
import { HandoffApiClientV1 } from '@chilipiper/api-client/src/handoff'
import { MeetingTypesApiClientV1 } from '@chilipiper/api-client/src/meeting-types'
import { partition } from 'remeda'
import invariant from 'tiny-invariant'
import { LinkResourceEnhanced } from '../types'
import { isGroupSchedulingLinkProductFeature } from '../types'
import { getCrmExplicitsFromMeetingAttemptProductFeature } from './crm-explicits'

export const getRoutingIdFromMeetingAttempt = (
  meetingAttempt?: MeetingAttemptResponseV3
): string | undefined => {
  if (
    meetingAttempt?.type !== 'NotFound' &&
    meetingAttempt?.attempt.productFeature.type === 'HandoffRouting'
  ) {
    return meetingAttempt.attempt.productFeature.routingId
  }

  return
}

const getLinkIdFromMeetingAttempt = (meetingAttempt?: MeetingAttemptResponseV3) => {
  if (!meetingAttempt || meetingAttempt?.type === 'NotFound') {
    return
  }

  if (
    meetingAttempt?.attempt.productFeature.type === 'RoundRobinSchedulingLink' ||
    meetingAttempt.attempt.productFeature.type === 'OwnershipSchedulingLink'
  ) {
    return meetingAttempt.attempt.productFeature.linkId
  }
}

export const getHostIdFromMeetingAttemptProductFeature = (
  productFeature?: ProductFeatureV3,
  groupSchedulingLinkHostId?: string
): string | undefined => {
  if (
    productFeature?.type === 'AdminIndividualSchedulingLink' ||
    productFeature?.type === 'PersonalIndividualSchedulingLink'
  ) {
    return productFeature.hostId
  }

  if (isGroupSchedulingLinkProductFeature(productFeature)) {
    if (productFeature?.hostId) {
      return productFeature.hostId
    }

    return groupSchedulingLinkHostId
  }

  return
}

export const getMeetingAttemptLinkResource = async (
  tenantId: string,
  handoffClient: HandoffApiClientV1,
  teamSchedulingClientV1: TeamSchedulingApiClientV1,
  teamSchedulingClientV2: TeamSchedulingApiClientV2,
  teamSchedulingClientV3: TeamSchedulingApiClientV3,
  meetingTypesClient: MeetingTypesApiClientV1,
  meetingAttempt?: MeetingAttemptResponseV3
): Promise<LinkResourceEnhanced | undefined> => {
  if (!meetingAttempt || meetingAttempt?.type === 'NotFound') {
    return
  }

  const productFeatureType = meetingAttempt.attempt.productFeature.type

  if (productFeatureType === 'HandoffRouting') {
    const routingId = getRoutingIdFromMeetingAttempt(meetingAttempt)
    const routing = routingId
      ? await handoffClient.getPublicRoutingInfo(tenantId, routingId)
      : undefined

    if (routing?.type !== 'PublicRoutingInfoForSelected') {
      return undefined
    }

    return {
      type: 'HandoffRouter',
      workspaceId: routing.workspaceId,
      meetingTypeIds: [routing.selectResponse.meetingTypeId],
      requiredAttendees: [],
      selectedAssignees: routing.selectResponse.selectedAssignees,
      selectedMeetingTypeId: routing.selectResponse.meetingTypeId,
      hostInfo: routing.selectResponse.host,
      primaryGuest: routing.primaryGuest,
    }
  }

  if (productFeatureType === 'OwnershipSchedulingLink') {
    const ownershipSchedulingLinkId = getLinkIdFromMeetingAttempt(meetingAttempt)

    const ownershipSchedulingLink = ownershipSchedulingLinkId
      ? await teamSchedulingClientV3.findAOwnershipSchedulingLinkByOwnershipSchedulingLinkIdPublicV3(
          tenantId,
          ownershipSchedulingLinkId
        )
      : undefined

    if (!ownershipSchedulingLink) {
      return Promise.reject(new Error('Invalid Ownership Scheduling Link'))
    }

    const routingPayload: OwnershipRoutingRequest | undefined = (() => {
      if (meetingAttempt.attempt.productFeature.primaryGuest?.crmId) {
        return {
          type: 'CrmIdRequest',
          crmId: meetingAttempt.attempt.productFeature.primaryGuest.crmId,
        }
      }
      if (meetingAttempt.attempt.productFeature.primaryGuest?.email) {
        return {
          type: 'GuestEmailRequest',
          guestEmail: meetingAttempt.attempt.productFeature.primaryGuest?.email,
        }
      }
    })()

    const routing = routingPayload
      ? await teamSchedulingClientV1.routeGuestToAOwnershipSchedulingLink(
          tenantId,
          ownershipSchedulingLink.linkId,
          {
            ...routingPayload,
            crmExplicits: getCrmExplicitsFromMeetingAttemptProductFeature(
              meetingAttempt.attempt.productFeature
            ),
          }
        )
      : undefined

    const commomData = {
      type: 'OwnershipSchedulingLink' as const,
      workspaceId: ownershipSchedulingLink.workspaceId,
      meetingTypeIds: ownershipSchedulingLink.meetingTypeIds,
      linkId: ownershipSchedulingLink.linkId,
    }

    if (routing && routing.assigneesEvaluationV3.type !== 'Distributed') {
      const { requiredAttendees: ownershipRequiredAttendees } = getAttendeesFromAlwaysInvitedUsers(
        ownershipSchedulingLink.ownership.alwaysInvitedUsers
      )
      return {
        ...commomData,
        ...ownershipSchedulingLink.ownership,
        requiredAttendees: ownershipRequiredAttendees,
        routing,
      }
    }

    const { requiredAttendees: distributionRequiredAttendees } = getAttendeesFromAlwaysInvitedUsers(
      ownershipSchedulingLink.distribution.alwaysInvitedUsers
    )

    return {
      ...commomData,
      ...ownershipSchedulingLink.distribution,
      requiredAttendees: distributionRequiredAttendees,
      routing,
    }
  }

  if (productFeatureType === 'RoundRobinSchedulingLink') {
    const roundRobinLinkId = getLinkIdFromMeetingAttempt(meetingAttempt)

    const roundRobinLink: PublicRoundRobinSchedulingLinkV2 | undefined = roundRobinLinkId
      ? await teamSchedulingClientV3.findARoundRobinSchedulingLinkByRoundRobinSchedulingLinkIdPublicV3(
          tenantId,
          roundRobinLinkId
        )
      : undefined

    if (!roundRobinLink) {
      return Promise.reject(new Error('Invalid Round Robin Link'))
    }

    const { requiredAttendees } = getAttendeesFromAlwaysInvitedUsers(
      roundRobinLink.alwaysInvitedUsers
    )

    return {
      type: 'RoundRobinSchedulingLink',
      workspaceId: roundRobinLink.workspaceId,
      meetingTypeIds: roundRobinLink.meetingTypeIds,
      requiredAttendees,
      bookerInvitation: roundRobinLink.bookerInvitation,
      host: roundRobinLink.host,
      assignments: roundRobinLink.assignments,
    }
  }

  if (productFeatureType === 'GroupSchedulingLink') {
    const groupSchedulingLink = await teamSchedulingClientV2
      .findAGroupSchedulingLinkByGroupSchedulingLinkIdPublicV2(
        tenantId,
        meetingAttempt.attempt.productFeature.linkId
      )
      .then(result => {
        // Adding the host to the additional invited users to avoid the host being excluded from the required attendees
        // and to avoid the host not being overridden by attendees while scheduling a meeting when host is optional
        return Promise.resolve({
          ...result,
          additionalInvitedUsers: [...(result.additionalInvitedUsers ?? []), result.host],
        })
      })
    const groupSchedulingLinkHostId = getHostIdFromMeetingAttemptProductFeature(
      meetingAttempt.attempt.productFeature,
      groupSchedulingLink.host.userId
    )

    invariant(groupSchedulingLinkHostId, 'Group Schedule Link - Host id not found')

    const { requiredAttendees, optionalAttendees } = getAttendeesFromAlwaysInvitedUsers(
      groupSchedulingLink.additionalInvitedUsers
    )

    return {
      type: 'GroupSchedulingLink',
      workspaceId: groupSchedulingLink.workspaceId,
      meetingTypeIds: groupSchedulingLink.meetingTypeIds,
      requiredAttendees: requiredAttendees ?? [],
      optionalAttendees: optionalAttendees ?? [],
      hostId: groupSchedulingLinkHostId,
      isHostOptional:
        groupSchedulingLinkHostId === groupSchedulingLink.host.userId &&
        groupSchedulingLink.host.optional,
    }
  }

  if (productFeatureType === 'AdminIndividualSchedulingLink') {
    const adminSchedulingLink =
      await teamSchedulingClientV1.findAAdminSchedulingLinkByAdminSchedulingLinkIdPublic(
        tenantId,
        meetingAttempt.attempt.productFeature.linkId
      )

    return {
      type: 'AdminSchedulingLink',
      workspaceId: adminSchedulingLink.workspaceId,
      meetingTypeIds: adminSchedulingLink.meetingTypeIds,
      requiredAttendees: [],
    }
  }

  if (productFeatureType === 'PersonalIndividualSchedulingLink') {
    const meetingTypeId = meetingAttempt.attempt.productFeature.meetingTypeId
    const meetingType = await meetingTypesClient
      .publicFindMeetingTypes(tenantId, { id: [meetingTypeId] })
      .then(result => result[meetingTypeId])

    return {
      type: 'PersonalSchedulingLink',
      workspaceId: meetingType.workspaceId,
      meetingTypeIds: [meetingType.id],
      requiredAttendees: [],
    }
  }
}

export const getAttendeesFromAlwaysInvitedUsers = (alwaysInvitedUsers?: AlwaysInvitedUser[]) => {
  const [requiredAttendees, optionalAttendees] = partition(
    alwaysInvitedUsers ?? [],
    user => !user.optional
  )
  return { requiredAttendees, optionalAttendees }
}
